import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Search, Download, CalendarCheck, SlidersHorizontal } from "lucide-react";
import { GlassCard, StatusBadge } from "../../components/common/ui";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { formatGBP } from "../../components/common/StockBadge";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const STATUS_LABEL = {
  RESERVATION_RECEIVED: "Received", UNDER_REVIEW: "Under Review", CONFIRMED: "Confirmed",
  READY_FOR_COLLECTION: "Ready for Collection", COLLECTED: "Collected", CANCELLED: "Cancelled",
};
const SUMMARY = [
  ["RESERVATION_RECEIVED", "Received"], ["UNDER_REVIEW", "Under Review"], ["CONFIRMED", "Confirmed"],
  ["READY_FOR_COLLECTION", "Ready"], ["COLLECTED", "Collected"], ["CANCELLED", "Cancelled"],
];
const selectCls = "bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm";
const MANAGERS = ["super_admin", "manager"];

export default function AdminReservations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);

  const get = (k, d = "") => params.get(k) || d;
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v == null) next.delete(k); else next.set(k, v);
    if (k !== "page") next.delete("page");
    setParams(next);
  };

  const load = useCallback(() => {
    setLoading(true);
    const q = { q: get("q"), status: get("status"), sort: get("sort", "created_desc"),
      collection_from: get("collection_from"), collection_to: get("collection_to"),
      created_from: get("created_from"), created_to: get("created_to"),
      promotion: get("promotion"), voucher: get("voucher"), page: get("page", "1"), page_size: 25 };
    Object.keys(q).forEach((k) => (q[k] === "" || q[k] == null) && delete q[k]);
    api.get("/admin/reservations", { params: q }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => setParam("q", search), 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const exportCsv = async () => {
    const q = { q: get("q"), status: get("status"), voucher: get("voucher"), collection_from: get("collection_from"), collection_to: get("collection_to") };
    Object.keys(q).forEach((k) => (q[k] === "") && delete q[k]);
    const res = await api.get("/admin/reservations/export", { params: q, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "reservations.csv"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const summary = data?.summary?.by_status || {};
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-reservations">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Reservations</h1>
          <p className="mt-1 text-muted-foreground text-sm">Review, edit and progress Click & Collect reservations.</p>
        </div>
        {MANAGERS.includes(user?.role) && <Button onClick={exportCsv} variant="outline" className="border-white/20 text-white gap-2" data-testid="export-csv-btn"><Download className="h-4 w-4" /> Export CSV</Button>}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {SUMMARY.map(([key, label]) => (
          <button key={key} onClick={() => setParam("status", get("status") === key ? "" : key)} data-testid={`summary-${key}`}
            className={`rounded-xl border p-4 text-left transition-colors ${get("status") === key ? "border-gold bg-gold/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
            <p className="font-playfair text-2xl text-white">{summary[key] ?? 0}</p>
            <p className="mt-1 text-xs text-muted-foreground">{label}</p>
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input data-testid="admin-res-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search number, name, email, mobile, product…" className="pl-9 bg-black/30 border-white/15 text-white" />
        </div>
        <select data-testid="admin-res-status" value={get("status")} onChange={(e) => setParam("status", e.target.value)} className={selectCls}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="admin-res-sort" value={get("sort", "created_desc")} onChange={(e) => setParam("sort", e.target.value)} className={selectCls}>
          <option value="created_desc">Newest</option><option value="created_asc">Oldest</option>
          <option value="collection_asc">Collection ↑</option><option value="collection_desc">Collection ↓</option>
          <option value="total_desc">Total ↓</option><option value="total_asc">Total ↑</option>
        </select>
        <Button variant="outline" className="border-white/20 text-white gap-2" onClick={() => setShowFilters((v) => !v)} data-testid="toggle-more-filters">
          <SlidersHorizontal className="h-4 w-4" /> More Filters
        </Button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-xl border border-white/10 bg-white/5 p-4" data-testid="more-filters-panel">
          <label className="text-xs text-white/60 space-y-1">Created from
            <input type="date" data-testid="filter-created-from" value={get("created_from")} onChange={(e) => setParam("created_from", e.target.value)} className={`${selectCls} w-full`} /></label>
          <label className="text-xs text-white/60 space-y-1">Created to
            <input type="date" data-testid="filter-created-to" value={get("created_to")} onChange={(e) => setParam("created_to", e.target.value)} className={`${selectCls} w-full`} /></label>
          <label className="text-xs text-white/60 space-y-1">Collection from
            <input type="date" data-testid="filter-collection-from" value={get("collection_from")} onChange={(e) => setParam("collection_from", e.target.value)} className={`${selectCls} w-full`} /></label>
          <label className="text-xs text-white/60 space-y-1">Collection to
            <input type="date" data-testid="filter-collection-to" value={get("collection_to")} onChange={(e) => setParam("collection_to", e.target.value)} className={`${selectCls} w-full`} /></label>
          <label className="text-xs text-white/60 space-y-1">Promotion
            <select data-testid="filter-promotion" value={get("promotion")} onChange={(e) => setParam("promotion", e.target.value)} className={`${selectCls} w-full`}>
              <option value="">Any</option><option value="yes">With promotion</option><option value="no">Without promotion</option></select></label>
          <label className="text-xs text-white/60 space-y-1">Coupon
            <select data-testid="filter-voucher" value={get("voucher")} onChange={(e) => setParam("voucher", e.target.value)} className={`${selectCls} w-full`}>
              <option value="">Any</option><option value="yes">With coupon</option><option value="no">Without coupon</option></select></label>
          <div className="flex items-end">
            <Button variant="outline" className="border-white/20 text-white w-full" data-testid="clear-filters"
              onClick={() => ["created_from", "created_to", "collection_from", "collection_to", "promotion", "voucher"].forEach((k) => setParam(k, ""))}>Clear</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <GlassCard className="p-12 flex flex-col items-center text-center" hover={false} data-testid="admin-res-empty">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-gold/10 border border-gold/20"><CalendarCheck className="h-7 w-7 text-gold" /></div>
          <p className="mt-5 font-playfair text-xl text-white">No reservations found</p>
          <p className="mt-2 text-sm text-muted-foreground">{get("q") || get("status") ? "Try adjusting your search or filters." : "Reservations will appear here once customers submit them."}</p>
        </GlassCard>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block overflow-hidden rounded-xl border border-white/10" data-testid="admin-res-table">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wide">
                <tr><th className="text-left p-3">Number</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Collection</th><th className="text-center p-3">Items</th><th className="text-right p-3">Total</th><th className="text-left p-3">Status</th></tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.reservation_number} onClick={() => navigate(`/admin/reservations/${r.reservation_number}`)} data-testid={`admin-res-row-${r.reservation_number}`} className="border-t border-white/5 hover:bg-white/5 cursor-pointer">
                    <td className="p-3 text-gold font-medium">{r.reservation_number}</td>
                    <td className="p-3 text-white/80"><div>{r.customer_name}</div><div className="text-white/40 text-xs">{r.customer_email}</div></td>
                    <td className="p-3 text-white/70">{r.collection_date}</td>
                    <td className="p-3 text-center text-white/70">{r.item_count}</td>
                    <td className="p-3 text-right text-white">{formatGBP(r.estimated_total)}{r.voucher_code && <span className="ml-1 text-emerald-400 text-xs">·{r.voucher_code}</span>}</td>
                    <td className="p-3"><StatusBadge status={STATUS_LABEL[r.status] || "Received"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {items.map((r) => (
              <div key={r.reservation_number} onClick={() => navigate(`/admin/reservations/${r.reservation_number}`)} data-testid={`admin-res-card-${r.reservation_number}`} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex justify-between items-start gap-2"><span className="text-gold font-semibold">{r.reservation_number}</span><StatusBadge status={STATUS_LABEL[r.status] || "Received"} /></div>
                <p className="mt-1 text-sm text-white/80">{r.customer_name} · {r.customer_email}</p>
                <p className="mt-1 text-xs text-white/50">Collection {r.collection_date} · {r.item_count} items · {formatGBP(r.estimated_total)}</p>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3" data-testid="admin-res-pagination">
              <Button variant="outline" className="border-white/20 text-white" disabled={data.page <= 1} onClick={() => setParam("page", String(data.page - 1))}>Previous</Button>
              <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages} · {data.total} total</span>
              <Button variant="outline" className="border-white/20 text-white" disabled={data.page >= data.pages} onClick={() => setParam("page", String(data.page + 1))}>Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
